import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  List,
  ListItem,
  Stack,
  Switch,
  Tab,
  TabList,
  Tabs,
  Text,
} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { LooseObject, Message, ServiceRecord, User, WorkshopUpdateRequest } from 'api/types';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiArrowRight } from 'react-icons/fi';
import { MdOutlineTranslate } from 'react-icons/md';
import * as yup from 'yup';

import { useUserContext } from '../../context/UserContextProvider';
import { textareaInput } from '../../helpers/makeFormFields';
import useConversationMessagesQuery from '../../hooks/useConversationMessagesQuery';
import { LoadingSpinner } from '../LoadingSpinner';
import AlertModal from '../modals/AlertModal';
import { ConversationBubble } from './ConversationBubble';

export interface IMessageForm {
  text: string;
}

const schema = yup
  .object({
    text: yup.string().required(),
  })
  .required();
const ConversationCard = (props: {
  record?: ServiceRecord | WorkshopUpdateRequest | LooseObject;
  sendMessage(x: any): void;
  statusCodes?: { [x: number]: string };
  canSendMessage: boolean;
  resourceKey: string;
  reasons: { [x: number]: string };
  setFile(file: Document): void;
  //@ts-ignore
  serviceRecordStatus?: number;
  updateStatus?: (data: any) => void;
  updateServiceRecordRequestStatusMutation?: (data: any) => void;
  assignee?: User | undefined;
  currentUserId?: number | undefined;
}) => {
  const {
    record = { conversation: {} },
    sendMessage,
    statusCodes = 999,
    canSendMessage,
    setFile,
    serviceRecordStatus,
    updateServiceRecordRequestStatusMutation,
    assignee,
    currentUserId,
  } = props;
  const { t } = useTranslation();
  const userContext = useUserContext();
  const userLanguage = userContext.user?.language;
  const workshopLanguage = record?.workshop?.language;
  const { conversation = {} } = record;

  // @ts-ignore
  const conversationMessagesQuery = useConversationMessagesQuery(conversation?.id);
  const taskAssignedToUser = assignee?.id === currentUserId;

  const messages = conversationMessagesQuery.data?.messages || [];
  const currentStatus = record?.status || 999;
  const [tabIndex, setTabIndex] = useState(0);

  enum messagePlaceholder {
    EN = 'Please type your message for the workshop.',
    DE = 'Bitte geben Sie Ihre Nachricht für den Workshop ein.',
    FR = "Veuillez saisir votre message pour l'atelier.",
    ES = 'Por favor, escriba su mensaje para el taller.',
    CS = 'Napište prosím svůj vzkaz pro workshop.',
    DA = 'Skriv venligst din besked til workshoppen.',
    IT = 'Scrivete il vostro messaggio per il workshop.',
    NO = 'Vennligst skriv inn meldingen din til workshopen.',
    NL = 'Typ je bericht voor de workshop.',
    SV = 'Vänligen skriv ditt meddelande till workshopen.',
    FI = 'Kirjoita viestisi työpajaa varten.',
  }

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    setError,
  } = useForm<IMessageForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const { mutate } = useMutation<any, Error, IMessageForm>({
    mutationFn: async (data) => {
      return sendMessage(data);
    },
  });

  const onSubmit: SubmitHandler<IMessageForm> = (data) => {
    const messageLanguage = tabIndex === 0 ? userLanguage.toUpperCase() : workshopLanguage.toUpperCase();
    const updatedData = { ...data, language: messageLanguage };
    mutate(updatedData, {
      onError: (error: any) => {
        const { errors } = error.response.data;

        (Object.keys(errors) as Array<keyof IMessageForm>).forEach((key) => {
          setError(key, {
            type: 'server',
            message: errors[key]!.join('. '),
          });
        });
      },
    });
  };
  if (conversationMessagesQuery.isLoading) {
    <LoadingSpinner />;
  }
  return (
    <Stack spacing={{ base: 3, lg: 6 }}>
      <FormControl display="flex" alignItems="center">
        <Switch
          id="email-alerts"
          // @ts-ignore
          isChecked={record?.auto_cancel_on_inactivity}
          onChange={() =>
            updateServiceRecordRequestStatusMutation &&
            // @ts-ignore
            updateServiceRecordRequestStatusMutation({ auto_cancel_on_inactivity: !record?.auto_cancel_on_inactivity })
          }
        />
        <FormLabel htmlFor="automatic-communication" mb="0" ml={3}>
          {t('components:conversation_card.message.automatic_communication')}
        </FormLabel>
      </FormControl>
      <Stack direction={'column'} gap={4}>
        {messages.map((message: Message, index: number) => {
          return (
            <Box width={'70%'} alignSelf={message?.user_id ? 'flex-end' : 'flex-start'} key={index}>
              <ConversationBubble
                // @ts-ignore
                statusCodes={statusCodes}
                file={message?.document?.file}
                currentStatus={currentStatus}
                messageObject={message}
                // @ts-ignore
                setFile={() => setFile(message?.document)}
              />
            </Box>
          );
        })}
      </Stack>
      {/* Conversation Form */}
      {canSendMessage && (
        <Box as="form" onSubmit={handleSubmit(onSubmit)} data-test-id="upload-form">
          <Divider borderWidth="1px" my={5} />
          <Stack mb={3} mt={5}>
            <Flex justifyContent="space-between">
              {serviceRecordStatus === 500 && (
                <Button
                  type="submit"
                  disabled={!taskAssignedToUser}
                  variant="primary"
                  mr={5}
                  onClick={() => {
                    NiceModal.show(AlertModal as any, {
                      onSubmit: () => {
                        if (updateServiceRecordRequestStatusMutation) {
                          updateServiceRecordRequestStatusMutation({ status: 200 });
                        }
                      },
                      children: (
                        <List>
                          <Text mb={'5'}>{t('components:conversation_card.message.modal.top_sentence')}</Text>
                          <ListItem>
                            <HStack justifyContent="left">
                              <Stack spacing={1}>
                                <Flex alignItems="center">
                                  <Text>{t('components:conversation_card.message.modal.bullet_point_one')}</Text>
                                </Flex>
                                <Flex alignItems="center">
                                  <Text>{t('components:conversation_card.message.modal.bullet_point_two')}</Text>
                                </Flex>
                                <Flex alignItems="center">
                                  <Text>{t('components:conversation_card.message.modal.bullet_point_three')}</Text>
                                </Flex>
                              </Stack>
                            </HStack>
                          </ListItem>
                          <Text mt={'5'}>{t('components:conversation_card.message.modal.bottom_sentence')}</Text>
                        </List>
                      ),
                      content: {
                        header: t('service_record:status.clarification.resolve_conversation.mark_as_resolved'),
                        footer: {
                          buttons: {
                            cancelCaption: t('common:cancel'),
                            actionCaption: t('service_record:status.clarification.resolve_conversation.mark_as_resolved'),
                          },
                        },
                      },
                    });
                  }}
                >
                  {t('service_record:status.clarification.resolve_conversation.mark_as_resolved')}
                </Button>
              )}
              {userLanguage.toLowerCase() !== workshopLanguage.toLowerCase() && (
                <Flex direction={'column'}>
                  <Flex justifyContent="center" alignItems="center" mr={2} wrap="wrap">
                    <Text mr={1.5} as="b" fontSize="sm">
                      {t(`components:conversation_card.message.response_language`)} :
                    </Text>{' '}
                    <Tabs variant="solid-rounded" colorScheme="green" index={tabIndex} onChange={handleTabsChange}>
                      <TabList>
                        <Tab color={'gray.400'} _selected={{ color: 'white', bg: 'blue.500', fontSize: 17 }}>
                          {t(`location:languages.${userLanguage.toUpperCase()}`)}
                        </Tab>
                        <Tab color={'gray.400'} _selected={{ color: 'white', bg: 'blue.500', fontSize: 17 }}>
                          {t(`location:languages.${workshopLanguage.toUpperCase()}`)}
                        </Tab>
                      </TabList>
                    </Tabs>
                  </Flex>
                  <Flex justifyContent="center" alignItems="center" wrap="wrap">
                    <Flex mr={1} alignItems={'center'} color={tabIndex === 0 ? 'gray.500' : 'transparent'}>
                      <MdOutlineTranslate />
                      <Text ml={1} fontSize={'11'}>
                        {t('service_record:status.clarification.conversation.message_to_translate')}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              )}
            </Flex>
          </Stack>

          {textareaInput({
            name: 'text',
            placeholder:
              //@ts-ignore
              messagePlaceholder[`${tabIndex === 0 ? userLanguage?.toUpperCase() : workshopLanguage.toUpperCase()}`],
            register,
            errors,
          })}

          <Flex direction="row-reverse" py="4" px={{ base: '2', md: '2' }}>
            <Button type="submit" variant="primary" disabled={!isValid} data-test-id="message-submit-button">
              {t('components:conversation_card.message.send_message.label')}
              <Icon as={FiArrowRight} boxSize="4" />
            </Button>
          </Flex>
        </Box>
      )}
    </Stack>
  );
};

export default ConversationCard;
